import { Helmet } from 'react-helmet-async';
import './landing-variants.css';

/* V15 — "Monolith Luxury"
   Extreme minimalism. Massive typography, very little color (ton-sur-ton),
   glacial-pace video-like background. Pure brand prestige.
   Cormorant (ultra-thin luxury serif) + Jost (geometric sans). */

const bg = '#111111';
const text = '#e8e4de';
const dim = '#555';
const subtle = '#333';
const accent = '#c9b99a'; /* muted gold, ton-sur-ton */

export default function LandingV15() {
  const serif = "'Cormorant', serif";
  const sans = "'Jost', sans-serif";

  return (
    <div className="doctif-page" style={{
      fontFamily: sans, background: bg, color: text,
      letterSpacing: '0.01em',
    }}>
      <Helmet><title>doctif — Monolith Luxury</title></Helmet>

      {/* Glacial-motion gradient overlay */}
      <div aria-hidden style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: `
          radial-gradient(ellipse 50% 40% at 50% 0%, rgba(201,185,154,0.04) 0%, transparent 70%),
          radial-gradient(ellipse 80% 50% at 50% 100%, rgba(0,0,0,0.5) 0%, transparent 60%)
        `,
      }} />

      {/* Very slow drift animation layer */}
      <div aria-hidden style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: `radial-gradient(circle 600px at 30% 40%, rgba(201,185,154,0.03) 0%, transparent 70%)`,
        animation: 'v15drift 30s ease-in-out infinite alternate',
      }} />
      <style>{`
        @keyframes v15drift {
          0% { transform: translate(0, 0) scale(1); }
          100% { transform: translate(80px, 40px) scale(1.1); }
        }
      `}</style>

      {/* ── Header — Absolute minimal ── */}
      <header className="doctif-shell" style={{ padding: '40px 0 24px', position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{
            fontFamily: serif, fontSize: 24, fontWeight: 300, letterSpacing: '0.3em',
            textTransform: 'uppercase', color: accent,
          }}>
            doctif
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <nav className="doctif-chip-row">
              {Array.from({ length: 15 }, (_, i) => (
                <a key={i} className="doctif-chip" href={`/${i + 1}/`} style={{
                  borderRadius: 0, padding: '3px 8px', fontSize: 10, fontWeight: 300,
                  letterSpacing: '0.05em',
                  background: i === 14 ? accent : 'transparent',
                  color: i === 14 ? bg : dim,
                  border: `1px solid ${i === 14 ? accent : subtle}`,
                }}>{String(i + 1).padStart(2, '0')}</a>
              ))}
            </nav>
            <a href="/accounts/login/" style={{
              fontFamily: sans, fontWeight: 300, fontSize: 12, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: accent, textDecoration: 'none',
              padding: '6px 16px', border: `1px solid ${accent}44`,
              transition: 'all 0.6s',
            }}>Accès</a>
          </div>
        </div>
      </header>

      {/* ── Hero — Massive Type ── */}
      <section className="doctif-shell" style={{
        position: 'relative', zIndex: 5,
        paddingTop: 80, paddingBottom: 100,
        minHeight: '70vh', display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}>
        <p className="doctif-fade-up" style={{
          fontFamily: sans, fontWeight: 300, fontSize: 12, letterSpacing: '0.4em',
          textTransform: 'uppercase', color: dim, marginBottom: 36,
        }}>
          Suite clinique
        </p>
        <h1 className="doctif-fade-up doctif-delay-1" style={{
          fontFamily: serif, fontWeight: 300,
          fontSize: 'clamp(4rem, 12vw, 11rem)',
          lineHeight: 0.88, letterSpacing: '-0.03em',
          marginBottom: 0, color: text,
        }}>
          doctif
        </h1>
        <div className="doctif-fade-up doctif-delay-2" style={{
          marginTop: 48, maxWidth: 400,
        }}>
          <p style={{
            fontSize: 15, lineHeight: 1.9, color: dim, fontWeight: 300,
            borderLeft: `1px solid ${accent}44`, paddingLeft: 20,
          }}>
            L'excellence clinique dans sa forme la plus pure.
            Pas de bruit. Pas d'ornement. Juste l'essentiel.
          </p>
        </div>
        <div className="doctif-fade-up doctif-delay-3" style={{ marginTop: 48 }}>
          <a href="/accounts/login/" style={{
            display: 'inline-block', padding: '14px 48px',
            border: `1px solid ${accent}`, color: accent,
            fontFamily: sans, fontWeight: 300, fontSize: 13, letterSpacing: '0.25em',
            textTransform: 'uppercase', textDecoration: 'none',
            transition: 'all 0.6s',
          }}>Entrer</a>
        </div>
      </section>

      {/* ── Thin accent line ── */}
      <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${accent}33, transparent)` }} />

      {/* ── Stats — Whisper quiet ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, padding: '60px 0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {[
            { v: '240+', l: 'Cabinets' },
            { v: '61k', l: 'Rendez-vous / mois' },
            { v: '34%', l: 'Temps économisé' },
          ].map((s, i) => (
            <div key={i} style={{
              textAlign: 'center', padding: '0 20px',
              borderRight: i < 2 ? `1px solid ${subtle}` : 'none',
            }}>
              <div style={{ fontFamily: serif, fontSize: 48, fontWeight: 300, color: text, letterSpacing: '-0.03em' }}>{s.v}</div>
              <div style={{ fontSize: 11, color: dim, letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: 8, fontWeight: 300 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${accent}22, transparent)` }} />

      {/* ── Features — Monolith blocks ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, padding: '80px 0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}>
          {[
            { title: 'Agenda', desc: 'Optimisation des créneaux, planification silencieuse.' },
            { title: 'Dossiers', desc: 'Historique clinique, accessible en un geste.' },
            { title: 'Facturation', desc: 'Flux continu, sans friction administrative.' },
            { title: 'Multi-cabinet', desc: 'Un point de contrôle pour chaque site.' },
          ].map((f, i) => (
            <div key={i} className="doctif-fade-up" style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40,
              padding: '40px 0', borderTop: `1px solid ${subtle}`,
              alignItems: 'baseline',
              animationDelay: `${i * 150}ms`,
            }}>
              <h3 style={{
                fontFamily: serif, fontWeight: 300,
                fontSize: 'clamp(2rem, 4vw, 3.6rem)',
                letterSpacing: '-0.02em', color: text,
              }}>{f.title}</h3>
              <p style={{ fontSize: 15, lineHeight: 1.8, color: dim, fontWeight: 300, maxWidth: 380 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Visual break — Full-width accent ── */}
      <div style={{ padding: '80px 0', position: 'relative', zIndex: 5 }}>
        <div className="doctif-shell" style={{ textAlign: 'center' }}>
          <p style={{
            fontFamily: serif, fontWeight: 300,
            fontSize: 'clamp(2.4rem, 6vw, 5rem)',
            letterSpacing: '-0.02em', lineHeight: 1.1,
            color: accent,
          }}>
            La forme suit<br />la rigueur.
          </p>
        </div>
      </div>

      {/* ── Testimonials — Ultra-minimal ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingBottom: 80 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0 }}>
          {[
            { q: 'Le silence de cette interface est éloquent.', a: 'Dr. Elise Moreau' },
            { q: 'C\'est comme entrer dans une galerie — chaque élément respire.', a: 'Dr. Julien Fabre' },
            { q: 'La sérénité de l\'outil se transmet au patient.', a: 'Dr. Nadia Hamdi' },
          ].map((t, i) => (
            <div key={i} style={{
              padding: '0 28px', borderRight: i < 2 ? `1px solid ${subtle}` : 'none',
            }}>
              <p style={{
                fontFamily: serif, fontStyle: 'italic', fontWeight: 300,
                fontSize: 18, lineHeight: 1.7, color: `${text}aa`, marginBottom: 20,
              }}>
                « {t.q} »
              </p>
              <p style={{ fontSize: 12, color: dim, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 300 }}>{t.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Trust ── */}
      <div style={{ borderTop: `1px solid ${subtle}`, borderBottom: `1px solid ${subtle}`, position: 'relative', zIndex: 5 }}>
        <div className="doctif-shell" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', padding: '20px 0', textAlign: 'center' }}>
          {[{ k: 'Chiffrement', v: 'AES-256' }, { k: 'Hébergement', v: 'HDS France' }, { k: 'Uptime', v: '99.9%' }, { k: 'Support', v: '< 2h' }].map((t, i) => (
            <div key={i} style={{ borderRight: i < 3 ? `1px solid ${subtle}` : 'none' }}>
              <div style={{ fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: dim, marginBottom: 4, fontWeight: 300 }}>{t.k}</div>
              <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 300, color: accent }}>{t.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA — Monumental ── */}
      <section className="doctif-shell" style={{
        position: 'relative', zIndex: 5,
        paddingTop: 100, paddingBottom: 100, textAlign: 'center',
      }}>
        <h2 style={{
          fontFamily: serif, fontWeight: 300,
          fontSize: 'clamp(2.4rem, 5.5vw, 4.5rem)',
          letterSpacing: '-0.02em', lineHeight: 1.05, marginBottom: 40,
          color: text,
        }}>
          Prêt ?
        </h2>
        <a href="/accounts/login/" style={{
          display: 'inline-block', padding: '16px 56px',
          border: `1px solid ${accent}`, color: accent,
          fontFamily: sans, fontWeight: 300, fontSize: 13, letterSpacing: '0.3em',
          textTransform: 'uppercase', textDecoration: 'none',
          transition: 'all 0.6s',
        }}>Commencer</a>
      </section>

      {/* ── Footer ── */}
      <footer style={{ position: 'relative', zIndex: 5, borderTop: `1px solid ${subtle}`, padding: '20px 0' }}>
        <div className="doctif-shell" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: serif, fontWeight: 300, fontSize: 13, color: dim, letterSpacing: '0.1em' }}>
            doctif — Direction Monolith Luxury
          </span>
          <span style={{ fontSize: 11, color: '#333', fontWeight: 300 }}>v15.0 — {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
